import type { FilmStock, GradePreset, LightingPreset } from '@studio/shared';

import { Carousel } from './Carousel';
import { OptionCard } from './OptionCard';
import { useExamples } from '../lib/useExamples';
import { stockSampleUrl } from '../lib/api';
import { usePresetLabel } from '../lib/i18n';

/**
 * Light and colour grade. Both were dropdowns, which is the wrong control for
 * a decision you make with your eyes — the difference between "golden hour"
 * and "overcast" is not a word, it is a picture.
 *
 * Until the examples are generated each card draws its own approximation: the
 * light as the gradient it casts, the grade as the palette it produces.
 */

export function LightPicker({
  lighting,
  selected,
  onChange,
}: {
  lighting: LightingPreset[];
  selected: string;
  onChange: (id: string) => void;
}) {
  const tp = usePresetLabel();
  const examples = useExamples();
  return (
    <Carousel count={`${lighting.length}`}>
      {lighting.map((light) => (
        <OptionCard
          key={light.id}
          title={tp(`light.${light.id}.name`, light.name)}
          description={tp(`light.${light.id}.desc`, light.description)}
          selected={selected === light.id}
          example={examples[`light-${light.id}`]}
          fallback={<LightSample id={light.id} />}
          ratio="4:5"
          onToggle={() => onChange(light.id)}
        />
      ))}
    </Carousel>
  );
}

/**
 * Film stock — how the frame is developed once it has been rendered.
 *
 * Every card is developed by the code that will develop your frames, so what
 * you are choosing from is the thing you will get — and the cards move as you
 * drag the grain. Once `npm run examples -- --stocks` has run, they develop a
 * real photograph instead of a test card: one frame developed seven ways, so
 * the only difference between the cards is the one you are choosing.
 */
export function StockPicker({
  stocks,
  selected,
  grain,
  onChange,
}: {
  stocks: FilmStock[];
  selected: string;
  grain: number;
  onChange: (stock: FilmStock) => void;
}) {
  const tp = usePresetLabel();
  return (
    <Carousel count={`${stocks.length}`}>
      {stocks.map((stock) => {
        // The chosen stock previews at your strength; the rest at their own.
        const strength = stock.id === selected ? grain : stock.grain;
        return (
          <OptionCard
            key={stock.id}
            title={tp(`stock.${stock.id}.name`, stock.name)}
            description={tp(`stock.${stock.id}.desc`, stock.description)}
            selected={selected === stock.id}
            image={stockSampleUrl(stock.id, strength)}
            ratio="4:5"
            onToggle={() => onChange(stock)}
          />
        );
      })}
    </Carousel>
  );
}

export function GradePicker({
  grades,
  selected,
  onChange,
}: {
  grades: GradePreset[];
  selected: string;
  onChange: (id: string) => void;
}) {
  const tp = usePresetLabel();
  const examples = useExamples();
  return (
    <Carousel count={`${grades.length}`}>
      {grades.map((grade) => (
        <OptionCard
          key={grade.id}
          title={tp(`grade.${grade.id}.name`, grade.name)}
          description={tp(`grade.${grade.id}.desc`, grade.description)}
          selected={selected === grade.id}
          example={examples[`grade-${grade.id}`]}
          fallback={<GradeSample id={grade.id} />}
          ratio="4:5"
          onToggle={() => onChange(grade.id)}
        />
      ))}
    </Carousel>
  );
}

/** A sphere lit the way the preset describes — the classic lighting diagram. */
function LightSample({ id }: { id: string }) {
  const light = LIGHT_SAMPLES[id] ?? LIGHT_SAMPLES['high-key-soft']!;
  return (
    <svg viewBox="0 0 100 125" aria-hidden="true" style={{ width: '100%', height: '100%' }}>
      <defs>
        <radialGradient id={`lg-${id}`} cx={light.cx} cy={light.cy} r="78%">
          <stop offset="0%" stopColor={light.hot} />
          <stop offset="62%" stopColor={light.mid} />
          <stop offset="100%" stopColor={light.dark} />
        </radialGradient>
      </defs>
      <rect width="100" height="125" fill={light.bg} />
      <circle cx="50" cy="58" r="30" fill={`url(#lg-${id})`} />
      <ellipse cx={light.shadowX} cy="94" rx="30" ry="6" fill="#000" opacity={light.shadow} />
    </svg>
  );
}

const LIGHT_SAMPLES: Record<
  string,
  {
    bg: string;
    hot: string;
    mid: string;
    dark: string;
    cx: string;
    cy: string;
    shadowX: number;
    shadow: number;
  }
> = {
  'high-key-soft': {
    bg: '#d9d5cd',
    hot: '#fffdf8',
    mid: '#e6e1d7',
    dark: '#c8c2b6',
    cx: '42%',
    cy: '34%',
    shadowX: 50,
    shadow: 0.08,
  },
  'soft-box-natural': {
    bg: '#b9b3a8',
    hot: '#fbf7ef',
    mid: '#ddd6c9',
    dark: '#8e8779',
    cx: '34%',
    cy: '30%',
    shadowX: 57,
    shadow: 0.2,
  },
  'editorial-contrast': {
    bg: '#4a453d',
    hot: '#fff8e9',
    mid: '#b9ad97',
    dark: '#2b2721',
    cx: '30%',
    cy: '26%',
    shadowX: 60,
    shadow: 0.45,
  },
  'hard-flash': {
    bg: '#8f8a80',
    hot: '#ffffff',
    mid: '#efece5',
    dark: '#6d675d',
    cx: '50%',
    cy: '42%',
    shadowX: 54,
    shadow: 0.55,
  },
  'window-daylight': {
    bg: '#b3b8ba',
    hot: '#f4f8fa',
    mid: '#cdd3d5',
    dark: '#7d8285',
    cx: '24%',
    cy: '32%',
    shadowX: 60,
    shadow: 0.24,
  },
  'rim-backlight': {
    bg: '#2e2b27',
    hot: '#ffe9bd',
    mid: '#6b6255',
    dark: '#211f1c',
    cx: '50%',
    cy: '78%',
    shadowX: 50,
    shadow: 0.3,
  },
  'overcast-soft': {
    bg: '#c2c4c2',
    hot: '#eef0ee',
    mid: '#d3d5d2',
    dark: '#a9aba8',
    cx: '48%',
    cy: '28%',
    shadowX: 50,
    shadow: 0.1,
  },
  'golden-hour': {
    bg: '#c9a172',
    hot: '#ffe4b0',
    mid: '#d8a978',
    dark: '#7d5533',
    cx: '72%',
    cy: '30%',
    shadowX: 34,
    shadow: 0.4,
  },
  'coastal-grey': {
    bg: '#aeb6b6',
    hot: '#e9eeee',
    mid: '#c3caca',
    dark: '#8b9393',
    cx: '46%',
    cy: '30%',
    shadowX: 52,
    shadow: 0.14,
  },
  'desert-sun': {
    bg: '#cfc3ac',
    hot: '#fffdf4',
    mid: '#ddd0b6',
    dark: '#7f7562',
    cx: '52%',
    cy: '18%',
    shadowX: 50,
    shadow: 0.5,
  },
  'blue-hour': {
    bg: '#3f4756',
    hot: '#9fb0c6',
    mid: '#5c6675',
    dark: '#272d38',
    cx: '50%',
    cy: '26%',
    shadowX: 50,
    shadow: 0.2,
  },
};

/** The palette a grade produces, as stacked bands. */
function GradeSample({ id }: { id: string }) {
  const bands = GRADE_SAMPLES[id] ?? GRADE_SAMPLES['clean-digital']!;
  return (
    <svg viewBox="0 0 100 125" aria-hidden="true" style={{ width: '100%', height: '100%' }}>
      {bands.map((colour, i) => (
        <rect
          key={colour + i}
          x="0"
          y={(125 / bands.length) * i}
          width="100"
          height={125 / bands.length}
          fill={colour}
        />
      ))}
    </svg>
  );
}

const GRADE_SAMPLES: Record<string, string[]> = {
  'clean-digital': ['#f4f1ec', '#d8c9b6', '#9c8a71', '#5b5348', '#211e1a'],
  'muted-editorial': ['#ddd8ce', '#b9b3a6', '#8b8579', '#5d584e', '#332f2a'],
  'warm-film': ['#f6e6cd', '#dcbb90', '#b08a5e', '#6f523a', '#2e241c'],
  'cool-nordic': ['#dfe3e0', '#b3bcb8', '#88938f', '#5b6462', '#2d3433'],
  'rich-contrast': ['#ffffff', '#d9a86a', '#8c4a2f', '#3b2418', '#000000'],
  'black-white': ['#ffffff', '#c4c4c4', '#8a8a8a', '#4a4a4a', '#000000'],
};
